import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { dataSearchState } from "../state";
import { verifyBulkActionPerformedAndAllow } from "../DataSearchUtils";

const runGuarded = performAction => {
  if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
    verifyBulkActionPerformedAndAllow(performAction);
  } else {
    performAction();
  }
};

/**
 * Searchable checkbox list with an Include / Exclude switch, stored as two id arrays in
 * filterComponentData (includeKey / excludeKey). Ticking an item adds it to the list for the
 * current mode (moving it out of the other one). Pass `groups` ([{ id, label, items: [{ id, label }] }])
 * for a grouped accordion, or `items` for a flat list.
 */
const IncludeExcludeAccordion = observer(
  ({ includeKey, excludeKey, groups, items, searchPlaceholder, loading = false, error = false, onRetry, emptyLabel }) => {
    const [mode, setMode] = useState("include");
    const [query, setQuery] = useState("");
    const [openGroups, setOpenGroups] = useState([]);
    const fd = dataSearchState.filterComponentData || {};
    const included = new Set(fd[includeKey] || []);
    const excluded = new Set(fd[excludeKey] || []);
    const current = mode === "include" ? included : excluded;
    const lower = query.trim().toLowerCase();
    const matches = item => !lower || `${item.id} ${item.label}`.toLowerCase().includes(lower);

    const grouped = Array.isArray(groups);
    const visibleGroups = grouped
      ? groups
          .map(g => {
            const groupMatch = lower && g.label.toLowerCase().includes(lower);
            return { ...g, visibleItems: groupMatch ? g.items : g.items.filter(matches) };
          })
          .filter(g => g.visibleItems.length > 0)
      : [];
    const visibleItems = grouped ? [] : (items || []).filter(matches);

    // Open every matching group while searching.
    useEffect(() => {
      setOpenGroups(lower && grouped ? visibleGroups.map(g => g.id) : []);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lower]);

    const setLists = (nextIncluded, nextExcluded) =>
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        [includeKey]: [...nextIncluded],
        [excludeKey]: [...nextExcluded]
      });

    const toggleItem = id =>
      runGuarded(() => {
        const inc = new Set(included);
        const exc = new Set(excluded);
        const target = mode === "include" ? inc : exc;
        const other = mode === "include" ? exc : inc;
        if (target.has(id)) {
          target.delete(id);
        } else {
          target.add(id);
          other.delete(id);
        }
        setLists(inc, exc);
      });

    const toggleGroup = group =>
      runGuarded(() => {
        const ids = group.visibleItems.map(i => i.id);
        const inc = new Set(included);
        const exc = new Set(excluded);
        const target = mode === "include" ? inc : exc;
        const other = mode === "include" ? exc : inc;
        if (ids.every(id => target.has(id))) {
          ids.forEach(id => target.delete(id));
        } else {
          ids.forEach(id => {
            target.add(id);
            other.delete(id);
          });
        }
        setLists(inc, exc);
      });

    const renderItem = item => {
      const inOther = mode === "include" ? excluded.has(item.id) : included.has(item.id);
      return (
        <label key={item.id} className="ftAcc__item" onClick={() => toggleItem(item.id)}>
          <span
            className={`ftAcc__checkbox${current.has(item.id) ? ` ftAcc__checkbox--checked${mode === "exclude" ? " ftAcc__checkbox--exclude" : ""}` : ""}`}
          />
          <span className="ftAcc__item-name">{item.label}</span>
          {inOther && <span className="ftAcc__item-tag">{mode === "include" ? "Excluded" : "Included"}</span>}
        </label>
      );
    };

    const nothingLoaded = grouped ? groups.length === 0 : (items || []).length === 0;

    return (
      <div className="ftAcc">
        <div className="titleModeToggle">
          <button
            type="button"
            className={`titleModeBtn${mode === "include" ? " titleModeBtn--active titleModeBtn--include" : ""}`}
            onClick={() => setMode("include")}
          >
            Include
          </button>
          <button
            type="button"
            className={`titleModeBtn${mode === "exclude" ? " titleModeBtn--active titleModeBtn--exclude" : ""}`}
            onClick={() => setMode("exclude")}
          >
            Exclude
          </button>
        </div>
        <div className="ftAcc__search">
          <input
            type="text"
            className="ftAcc__search-input"
            placeholder={searchPlaceholder}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query ? (
            <i className="material-icons ftAcc__search-icon ftAcc__search-clear" onClick={() => setQuery("")}>
              close
            </i>
          ) : (
            <i className="material-icons ftAcc__search-icon">search</i>
          )}
        </div>
        <div className="ftAcc__list ftAcc__list--tall">
          {nothingLoaded ? (
            error ? (
              <div className="ftAcc__empty">
                Couldn't load {emptyLabel}.{" "}
                <a
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    onRetry && onRetry();
                  }}
                >
                  Retry
                </a>
              </div>
            ) : (
              <div className="ftAcc__empty">{loading ? `Loading ${emptyLabel}…` : `No ${emptyLabel}`}</div>
            )
          ) : (grouped ? visibleGroups.length === 0 : visibleItems.length === 0) ? (
            <div className="ftAcc__empty">No results found</div>
          ) : grouped ? (
            visibleGroups.map(group => {
              const ids = group.visibleItems.map(i => i.id);
              const checkedCount = ids.filter(id => current.has(id)).length;
              const allChecked = checkedCount === ids.length;
              const isOpen = openGroups.includes(group.id);
              const exclude = mode === "exclude" ? " ftAcc__checkbox--exclude" : "";
              return (
                <div key={group.id} className="ftAcc__cat">
                  <div className="ftAcc__cat-row">
                    <button
                      type="button"
                      className="ftAcc__cat-expand"
                      onClick={() =>
                        setOpenGroups(prev =>
                          prev.includes(group.id) ? prev.filter(g => g !== group.id) : [...prev, group.id]
                        )
                      }
                    >
                      <span className="material-icons">{isOpen ? "keyboard_arrow_down" : "keyboard_arrow_right"}</span>
                    </button>
                    <label className="ftAcc__cat-label" onClick={() => toggleGroup(group)}>
                      <span
                        className={`ftAcc__checkbox${
                          allChecked
                            ? ` ftAcc__checkbox--checked${exclude}`
                            : checkedCount > 0
                            ? ` ftAcc__checkbox--indeterminate${exclude}`
                            : ""
                        }`}
                      />
                      <span className="ftAcc__cat-name">{group.label}</span>
                    </label>
                  </div>
                  {isOpen && <div className="ftAcc__items">{group.visibleItems.map(renderItem)}</div>}
                </div>
              );
            })
          ) : (
            visibleItems.map(renderItem)
          )}
        </div>
      </div>
    );
  }
);

export default IncludeExcludeAccordion;
